import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, RotateCcw, RefreshCw, ExternalLink, X, Loader2, Search } from "lucide-react";

// Currency symbol table — mirrors the backend PDF service.
const SYMBOLS = { INR: "₹", USD: "$", GBP: "£", EUR: "€" };

const STATUS_STYLE = {
  paid: "bg-[#dcfce7] text-[#166534] border-[#16a34a]",
  refunded: "bg-[#fef3c7] text-[#92400e] border-[#f59e0b]",
  partially_refunded: "bg-[#fef3c7] text-[#92400e] border-[#f59e0b]",
  failed: "bg-[#fee2e2] text-[#991b1b] border-[#dc2626]",
  initiated: "bg-[#e0e7ff] text-[#3730a3] border-[#4f46e5]",
  unpaid: "bg-[#f4f4f5] text-[#52525b] border-[#a1a1aa]",
  mismatch: "bg-[#fee2e2] text-[#991b1b] border-[#dc2626]",
};

export default function PaymentsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [refundFor, setRefundFor] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await api.get("/admin/payments/paypal", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const sync = async (id) => {
    try {
      await api.get(`/admin/payments/paypal/${id}/sync`);
      toast.success("Synced with PayPal");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      {/* Heading + summary stats */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c] flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> LIVE PayPal Transactions</div>
          <h2 className="font-display text-2xl md:text-3xl font-black">Payment History</h2>
          <div className="text-sm text-[#71717a] mt-1">Every PayPal-paid order · fees · exchange rate · refund controls</div>
        </div>
        <button onClick={load} disabled={busy} className="border-2 border-[#09090b] px-3 py-1.5 text-xs font-bold hover:bg-[#09090b] hover:text-white disabled:opacity-60 flex items-center gap-1.5" data-testid="payments-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Stats cards */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.currency} className="border-2 border-[#09090b] p-4 bg-[#fff7ed]">
              <div className="text-[10px] uppercase tracking-widest text-[#71717a] font-bold">Paid ({s.currency})</div>
              <div className="text-2xl font-black font-display mt-1">{SYMBOLS[s.currency] || s.currency}{s.gross.toFixed(2)}</div>
              <div className="text-[11px] text-[#71717a] mt-1">{s.count} orders · Fee {SYMBOLS[s.currency] || s.currency}{s.fee.toFixed(2)} · Net <b>{SYMBOLS[s.currency] || s.currency}{s.net.toFixed(2)}</b></div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center border-2 border-[#e4e4e7] focus-within:border-[#09090b] flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 mx-2 text-[#71717a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Order #, capture id, or email" className="flex-1 py-2 text-sm outline-none" data-testid="payments-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border-2 border-[#e4e4e7] px-3 py-2 text-sm" data-testid="payments-status-filter">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="partially_refunded">Partially Refunded</option>
          <option value="failed">Failed</option>
          <option value="initiated">Initiated</option>
          <option value="mismatch">Amount Mismatch</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border-2 border-[#09090b]">
        <table className="w-full text-sm" style={{ minWidth: '1100px' }}>
          <thead className="bg-[#09090b] text-white uppercase text-[10px] tracking-wider">
            <tr>
              <th className="p-3 text-left">Order #</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">PayPal Capture</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3 text-right">Fee</th>
              <th className="p-3 text-right">Net</th>
              <th className="p-3 text-center">FX Rate</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-center">Paid At</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => {
              const sym = SYMBOLS[o.currency] || o.currency;
              const cls = STATUS_STYLE[o.payment_status] || "bg-[#f4f4f5] text-[#52525b] border-[#a1a1aa]";
              return (
                <tr key={o.id} className="border-t border-border" data-testid={`payment-row-${o.order_number.replace(/\//g,'_')}`}>
                  <td className="p-3 font-mono font-bold text-xs">{o.order_number}</td>
                  <td className="p-3">
                    <div className="text-xs font-bold">{o.customer_name || "—"}</div>
                    <div className="text-[10px] text-[#71717a]">{o.customer_email || o.payer_email || "—"}</div>
                  </td>
                  <td className="p-3">
                    {o.paypal_capture_id ? (
                      <div className="text-[10px] font-mono">{o.paypal_capture_id}</div>
                    ) : <span className="text-[10px] text-[#a1a1aa]">no capture</span>}
                    {o.payer_email && <div className="text-[10px] text-[#71717a]">Payer: {o.payer_email}</div>}
                  </td>
                  <td className="p-3 text-right font-mono">{sym}{o.amount.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-[#dc2626]">{o.paypal_fee > 0 ? `${sym}${o.paypal_fee.toFixed(2)}` : "—"}</td>
                  <td className="p-3 text-right font-mono font-bold text-[#16a34a]">{o.paypal_net_amount > 0 ? `${sym}${o.paypal_net_amount.toFixed(2)}` : "—"}</td>
                  <td className="p-3 text-center text-[11px] text-[#71717a]">{o.paypal_exchange_rate || "—"}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border ${cls}`}>{(o.payment_status || "—").replace("_", " ")}</span>
                    {o.refund_amount > 0 && (
                      <div className="text-[9px] text-[#92400e] mt-0.5">Refunded {sym}{o.refund_amount.toFixed(2)}</div>
                    )}
                  </td>
                  <td className="p-3 text-center text-[10px] text-[#71717a]">{o.paid_at ? new Date(o.paid_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="p-3 text-center whitespace-nowrap">
                    {o.paypal_capture_id && (
                      <>
                        <button onClick={() => sync(o.id)} className="p-1.5 hover:bg-[#f4f4f5]" title="Sync from PayPal" data-testid={`sync-${o.order_number.replace(/\//g,'_')}`}><RefreshCw className="w-4 h-4" /></button>
                        {["paid", "partially_refunded"].includes(o.payment_status) && (
                          <button onClick={() => setRefundFor(o)} className="p-1.5 hover:bg-[#fef3c7] text-[#92400e]" title="Refund" data-testid={`refund-${o.order_number.replace(/\//g,'_')}`}><RotateCcw className="w-4 h-4" /></button>
                        )}
                        <a href={`https://www.paypal.com/activity/payment/${o.paypal_capture_id}`} target="_blank" rel="noreferrer" className="inline-block p-1.5 hover:bg-[#f4f4f5]" title="Open in PayPal"><ExternalLink className="w-4 h-4" /></a>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-[#71717a] text-sm"><CreditCard className="w-8 h-8 mx-auto mb-2 text-[#a1a1aa]" />No PayPal transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#71717a]">Showing {items.length} of {total} PayPal orders</div>

      {refundFor && <RefundModal order={refundFor} onClose={() => setRefundFor(null)} onDone={() => { setRefundFor(null); load(); }} />}
    </div>
  );
}

function RefundModal({ order, onClose, onDone }) {
  const sym = SYMBOLS[order.currency] || order.currency;
  const alreadyRefunded = Number(order.refund_amount || 0);
  const remaining = Math.max(0, Number(order.amount) - alreadyRefunded);
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const numeric = Number(amount);
    if (!full) {
      if (!(numeric > 0) || numeric > remaining) {
        toast.error(`Enter an amount between ${sym}0.01 and ${sym}${remaining.toFixed(2)}`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = { note };
      if (!full) payload.amount = numeric;
      const { data } = await api.post(`/admin/payments/paypal/${order.id}/refund`, payload);
      toast.success(`Refund ${data.status?.toLowerCase() || "issued"} — ${sym}${data.amount?.toFixed(2)}`);
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="refund-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-md border-2 border-[#09090b]">
        <div className="p-3 border-b-2 border-[#09090b] flex items-center justify-between bg-[#fef3c7]">
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#92400e]">Refund via PayPal</div>
            <div className="font-display text-lg font-black">{order.order_number}</div>
          </div>
          <button type="button" onClick={onClose} disabled={busy}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="border border-border p-2">
              <div className="text-[10px] text-[#71717a] uppercase font-bold">Order Amount</div>
              <div className="font-mono font-bold">{sym}{order.amount.toFixed(2)}</div>
            </div>
            <div className="border border-border p-2">
              <div className="text-[10px] text-[#71717a] uppercase font-bold">Refundable</div>
              <div className="font-mono font-bold text-[#16a34a]">{sym}{remaining.toFixed(2)}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-1.5 text-sm"><input type="radio" checked={full} onChange={() => { setFull(true); setAmount(remaining.toFixed(2)); }} /> Full refund</label>
            <label className="inline-flex items-center gap-1.5 text-sm"><input type="radio" checked={!full} onChange={() => setFull(false)} /> Partial</label>
          </div>
          {!full && (
            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Amount ({order.currency})</div>
              <input type="number" min="0.01" step="0.01" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 text-sm outline-none" data-testid="refund-amount" />
            </label>
          )}
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Note to buyer (optional)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={255} placeholder="Reason for the refund…" className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 text-sm outline-none" data-testid="refund-note" />
          </label>
        </div>
        <div className="p-3 border-t-2 border-[#09090b] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs font-bold border-2 border-[#e4e4e7] hover:border-[#09090b] disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-1.5 text-xs font-bold bg-[#dc2626] hover:bg-[#991b1b] text-white disabled:opacity-60 flex items-center gap-1.5" data-testid="refund-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            {busy ? "Processing…" : "Confirm Refund"}
          </button>
        </div>
      </form>
    </div>
  );
}
